import { useState } from 'react';
import { useDeleteInteractionLog, useGetInteractionsLogs } from 'apis/business/agent/callLogs/hook';
import { Box, IconButton, Theme, Typography, useTheme } from '@mui/material';
import Search from 'components/Common/Search';
import Select from 'components/Common/Select';
import { useForm } from 'react-hook-form';
import DataTable from 'components/Common/DataTable';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { ColumnDef } from '@tanstack/react-table';
import { formatDate } from 'utils/helperFunctions';
import { FaRegCirclePlay } from 'react-icons/fa6';
import { Link, Outlet } from 'react-router-dom';
import Pagination from 'components/Common/Pagination';
import DateRangePicker from 'components/Common/DateRangePicker';
import { SlScreenDesktop } from 'react-icons/sl';
import { IoCallOutline } from 'react-icons/io5';

export default function CallLogs() {
  const theme = useTheme();
  const { control, watch } = useForm({
    defaultValues: {
      source_eq: 'all'
    }
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState<null | string>(null);
  const filters: Record<string, any> = watch();

  if (filters.source_eq === 'all') {
    delete filters.source_eq;
  } else {
    filters['filter[source_eq]'] = filters.source_eq;
    delete filters.source_eq;
  }
  const updatedFilters = {
    ...filters
  };
  const { data: callLogs, isLoading: isCallLogsLoading } = useGetInteractionsLogs({
    ...{ ...updatedFilters, 'filter[source_info_cont]': searchText },
    page: currentPage
  });

  const { mutate: deleteCallLog } = useDeleteInteractionLog();

  return (
    <>
      <Box display='flex' justifyContent='space-between' alignItems='center' marginBottom={3}>
        <Typography variant='h2' fontWeight={500}>
          Call Logs
        </Typography>
        <Box display='flex' gap={2}>
          <Select
            name='source_eq'
            label='Call type'
            defaultValue='all'
            control={control}
            options={[
              { value: 'all', label: 'All' },
              { value: 'phone', label: 'Phone Call' },
              { value: 'web', label: 'Web' },
              { value: 'sms', label: 'Personalised Link' }
            ]}
            sx={{ height: '40px', background: theme.palette.background.paper }}
          />
          {[0, 'all'].includes(watch('source_eq')) && (
            <Search
              size='small'
              sx={{
                minWidth: 220,
                '& .MuiInputBase-root': {
                  backgroundColor: theme.palette.background.paper
                }
              }}
              searchText={searchText}
              setSearchText={setSearchText}
              placeholder='Search by number'
            />
          )}
          {/* <DatePicker name='filter[calls_received_in]' control={control} size='small' /> */}
          <Box sx={{ minWidth: '270px' }}>
            <DateRangePicker name='filter[calls_received_in]' control={control} />
          </Box>
        </Box>
      </Box>
      <DataTable
        data={callLogs?.data}
        columns={columns(theme, deleteCallLog, control)}
        isHeaderSticky={true}
        isLoading={isCallLogsLoading}
      />
      <Pagination totalPages={callLogs?.meta?.total_pages} currentPage={currentPage} onChange={setCurrentPage} />
      <Outlet />
    </>
  );
}

type DeleteCallLog = (id: number) => void;

const columns = (theme: Theme, deleteCallLog: DeleteCallLog, control: any): ColumnDef<any>[] => [
  {
    header: 'SOURCE / PHONE NUMBER',
    cell: (info) =>
      info.row.original?.source_type === 'web' ? (
        <Box display='flex' gap={1} alignContent='center'>
          <Box marginTop='3px'>
            <SlScreenDesktop />
          </Box>
          <Typography>Test Call (Web)</Typography>
          <Typography
            fontSize={12}
            borderRadius={1}
            paddingX={1}
            border={`1px solid ${theme.palette.primary.light}`}
            bgcolor={theme.palette.primary.light}
            color={theme.palette.primary.main}
          >
            Test
          </Typography>
        </Box>
      ) : (
        <Box display='flex' gap={1}>
          <Box marginTop='3px'>
            <IoCallOutline />
          </Box>
          <Typography>{info.row.original.details?.from_phone}</Typography>
          {/* TODO - @need to work later */}
          {/* <Typography fontWeight='500' marginBottom={1}>
          Call Journey:
        </Typography>
        <Box display='flex' alignItems='center'>
          <Box display='flex' alignItems='center'>
            <Typography
              bgcolor='#ECECEC'
              borderRadius='8px'
              padding='2px 16px'
              textTransform='uppercase'
              fontSize='12px'
            >
              GREETING
            </Typography>
            <Box color='#777'>
              <FaArrowRightLong />
            </Box>
            <Typography
              bgcolor='#ECECEC'
              borderRadius='8px'
              padding='2px 16px'
              textTransform='uppercase'
              fontSize='12px'
            >
              FALLBACK
            </Typography>
            <Box color='#777'>
              <FaArrowRightLong />
            </Box>
            <Typography
              bgcolor='#ECECEC'
              borderRadius='8px'
              padding='2px 16px'
              textTransform='uppercase'
              fontSize='12px'
            >
              GOODBYE
            </Typography>
          </Box>
        </Box> */}
        </Box>
      )
  },
  {
    header: 'DATE & TIME',
    cell: (info) => <Typography>{formatDate(info.row.original.details?.start_at)}</Typography>
  },
  {
    header: 'Direction',
    cell: (info) => (
      <Typography
        sx={{
          display: 'inline-block',
          textTransform: 'capitalize',
          fontSize: 12,
          borderRadius: 3,
          px: 1,
          color: info.row.original.direction === 'inbound' ? '#166537' : '#1e40AF',
          bgcolor: info.row.original.direction === 'inbound' ? '#DCFCE7' : '#DBEAFE'
        }}
      >
        {info.row.original.direction}
      </Typography>
    )
  },
  {
    header: 'DURATION',
    accessorKey: 'details.duration'
  },
  {
    header: 'CALL TYPE',
    cell: (info) => <Typography sx={{ textTransform: 'capitalize' }}>{info.row.original.source_type}</Typography>
  },
  {
    header: 'Action',
    cell: (info) => (
      <Box display='flex' gap={1}>
        <Box fontSize={12} borderRadius='50%' bgcolor='#E5E7EB'>
          <Link to={String(info.row.original.id)}>
            <IconButton>
              <FaRegCirclePlay size={16} />
            </IconButton>
          </Link>
        </Box>
        <Box fontSize={12} borderRadius='50%' bgcolor='#E5E7EB'>
          <IconButton onClick={() => deleteCallLog(info.row.original.id)} color='error'>
            <RiDeleteBin6Line size={16} style={{ color: 'red' }} />
          </IconButton>
        </Box>
      </Box>
    )
  }
];
